import { Accordion, Button, Dropdown, DropdownItem, Icon, IconProps } from '@platform/components';
import cx from 'classix';
import Skeleton from 'react-loading-skeleton';
import { useMixpanelTrackLink } from '../analytics/mixpanel';
import FlexibleLink from '../util/FlexibleLink';

interface NavbarDropdownProps {
  avatar?: string;
  avatarFallbackIcon?: IconProps['type'];
  buttonText: string;
  isLoading: boolean;
}

export interface NavbarDropdownItem {
  title: string;
  href: string;
  eventName?: string;
  active?: boolean;
  additionalClassnames?: string;
  newTab?: boolean;
  // TODO: I believe this currently doesn't work due to switchover to <Dropdown>; need to reconsider
  linkType: Parameters<typeof FlexibleLink>[0]['type'];
}

/** Handles using mixpanel link tracking with a forwarded ref for the navbar dropdown.
 * We'll need to rework this into the Dropdown component properly if we need to use similar
 * <a> tag navigating dropdowns elsewhere */
const NavbarDropdownMenuItem = (
  props: Parameters<typeof DropdownItem>[0] & { eventName?: string },
) => {
  const setMpRef = useMixpanelTrackLink<HTMLAnchorElement>(props.eventName || '');
  return <DropdownItem {...props} ref={setMpRef} />;
};

const avatarSizeInPx = {
  xl: '32',
  '2xl': '48',
};
type NavbarDropdownAvatarProps = {
  avatar: string;
  avatarFallbackIcon: IconProps['type'];
  size: 'xl' | '2xl';
};

const NavbarDropdownAvatar = ({ avatar, avatarFallbackIcon, size }: NavbarDropdownAvatarProps) => {
  return (
    <>
      {!avatar && (
        <div
          className={cx(
            'bg-gray-100 rounded-full flex items-center justify-center',
            size === 'xl' && 'w-[32px] min-w-[32px] h-[32px]',
            size === '2xl' && 'w-[48px] min-w-[48px] h-[48px]',
          )}
        >
          <span className='border-b-white underline-offset-[6px] decoration-2 group-hover:underline'>
            {/* Empty onClick enables cursor-pointer on the Icon */}
            <Icon type={avatarFallbackIcon} size='24' onClick={() => {}} />
          </span>
        </div>
      )}
      {avatar && (
        <img
          className='shrink-0 rounded-full overflow-hidden object-cover'
          width={avatarSizeInPx[size]}
          height={avatarSizeInPx[size]}
          src={avatar ?? ''}
          alt='user-profile'
        />
      )}
    </>
  );
};

const NavbarDropdownButton = ({
  avatar,
  avatarFallbackIcon,
  buttonText,
  isLoading,
}: NavbarDropdownProps) => (
  // Using flex here causes an issue where the chevron icon is not calculated into the width of the dropdown button,
  // which causes overflow issues when the button's width is constrained by its container
  <div className='flex items-center md:contents group truncate'>
    {avatar && avatarFallbackIcon && (
      <NavbarDropdownAvatar avatar={avatar} avatarFallbackIcon={avatarFallbackIcon} size='2xl' />
    )}
    <span className='grow-0 shrink mx-2 md:font-bold underline-offset-8 decoration-2 group-hover:underline overflow-x-hidden text-ellipsis'>
      {!isLoading ? buttonText : <Skeleton width={300} />}
    </span>
  </div>
);

/** Implements a DS Dropdown component for use in the Dashboard Navbar. This is a short-term specialization of Dropdown
 * due to not conforming to the DS spec simultaneous to us not yet having capacity to redesign the Dashboard nav experience. */
const NavbarDropdown = ({
  avatar,
  avatarFallbackIcon,
  buttonText,
  isLoading,
  items,
  dataTestId,
  buttonPropsOverride,
  additionalTitleClassnames,
}: {
  avatar?: string;
  avatarFallbackIcon?: IconProps['type'];
  buttonText: string;
  isLoading: boolean;
  items: NavbarDropdownItem[];
  dataTestId: string;
  additionalTitleClassnames?: string;
  buttonPropsOverride?: Parameters<typeof Button>[0];
}) => {
  const getDesktopHref = (menuItem: NavbarDropdownItem) => {
    if (menuItem.linkType === 'internal') {
      return `/platform${menuItem.href}`;
    }
    if (!import.meta.env.SSR && window) {
      return `${window.location.origin}${menuItem.href}`;
    } else {
      return menuItem.href;
    }
  };

  return (
    <>
      <div className='md:hidden'>
        <Accordion
          type='custom'
          className='p-4 pl-2'
          leftContent={
            avatar && avatarFallbackIcon ?
              <NavbarDropdownAvatar
                avatar={avatar}
                avatarFallbackIcon={avatarFallbackIcon}
                size='xl'
              />
            : undefined
          }
          titleContent={
            <div
              className={`ml-[-4px] text-sm text-gray-500 font-medium truncate ${additionalTitleClassnames}`}
            >
              {buttonText}
            </div>
          }
          isBorderless
          initiallyClosed
        >
          {items.map((menuItem) => (
            <div key={`${menuItem.title}-menuItem`} className='w-full text-base'>
              <FlexibleLink
                newTab={menuItem.newTab}
                href={menuItem.href}
                type={menuItem.linkType}
                eventName={menuItem.eventName}
                className={`w-full p-2 pl-4 block ${menuItem.additionalClassnames}`}
              >
                {menuItem.title}
              </FlexibleLink>
            </div>
          ))}
        </Accordion>
      </div>
      <div className='hidden md:block min-w-0'>
        <Dropdown
          type='button'
          ariaLabel='Profile Dropdown'
          buttonProps={
            buttonPropsOverride ?? {
              dataTestId,
              variant: 'tertiary-gray',
              size: 'lg',
              /* Disable the default tertiary-gray button styles. */
              children: (
                <NavbarDropdownButton
                  avatarFallbackIcon={avatarFallbackIcon}
                  avatar={avatar}
                  buttonText={buttonText}
                  isLoading={isLoading}
                />
              ),
            }
          }
        >
          {items.map((menuItem) => (
            <NavbarDropdownMenuItem
              key={menuItem.title}
              href={getDesktopHref(menuItem)}
              isActive={menuItem.active}
              eventName={menuItem.eventName}
              additionalClassnames={
                menuItem.active ?
                  `underline underline-offset-[.4rem] ${menuItem.additionalClassnames}`
                : menuItem.additionalClassnames
              }
            >
              {menuItem.title}
            </NavbarDropdownMenuItem>
          ))}
        </Dropdown>
      </div>
    </>
  );
};

export default NavbarDropdown;
